import React, { useEffect, useState } from 'react'
import './profile.styles.scss'

import ProfileIcon from './profile-icon.component'
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { UseCloudFunction } from '@/utils/firebase.utils';

import BrandLogo from '@/components/SHARED/brand-logo/brand-logo.component';
import { ModalAlert } from '@/components/modal/modal.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';

const NAME_MAX = 40;

const ProfilePage = () => {

    const currentUser = useSelector(state => state.userData.currentUser);
    const userProfile = useSelector(state => state.userData.userProfile);
    const [ name, setName ] = useState('');
    const [ icon, setIcon ] = useState({});
    const [ loading, setLoading ] = useState(false);
    const [ alertMessage, setAlertMessage ] = useState(null);
    const navigate = useNavigate();
    const editing = Boolean(userProfile?.name);

    // Start from the saved profile, else from the sign-in account (email
    // sign-ups set a display name; Google gives a name and a photo).
    useEffect(() => {
        if (!currentUser) return;
        setName(userProfile?.name || currentUser.displayName || '');
        const url = userProfile ? userProfile.icon : currentUser.photoURL;
        setIcon(url ? { url } : {});
    }, [currentUser, userProfile])

    async function uploadIcon(blob) {
        const iconRef = ref(getStorage(),
            `uploaded-profile-icons/${currentUser.uid}-${Date.now()}.jpg`);
        await uploadBytes(iconRef, blob, { contentType: 'image/jpeg' });
        return getDownloadURL(iconRef);
    }

    async function handleSubmit() {
        const trimmed = name.trim();
        if (!trimmed) {
            setAlertMessage('Please enter a screen name.');
            return;
        }
        setLoading(true);
        let url = icon.url ?? '';
        if (icon.blob) {
            try {
                url = await uploadIcon(icon.blob);
            } catch (err) {
                console.log(err);
                setLoading(false);
                setAlertMessage("Your picture couldn't be uploaded. Try another one, or save without it.");
                return;
            }
        }
        const res = await UseCloudFunction('createProfile', { obj: { name: trimmed, icon: url } });
        if (res.error) {
            setLoading(false);
            setAlertMessage(`Something went wrong. Try again later. (Error: ${res.error})`);
            return;
        }
        // Loads the saved profile and picks up any new token claims.
        navigate('/loading/refresh');
    }

    if (!currentUser) return null;

    return (
        <div className='profile-page'>
            <div className='profile-logo'><BrandLogo theme='hardwood' tone='light' /></div>
            <form className='g-card' onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                <h2 className='profile-title'>{editing ? 'Your profile' : 'Create your profile'}</h2>
                {!editing &&
                    <p className='profile-intro'>Pick the name other managers will see next to your team.</p>
                }
                <div className='field'>
                    <label htmlFor='profile-name'>Screen name</label>
                    <input id='profile-name' type='text' value={name} maxLength={NAME_MAX}
                        autoComplete='nickname' onChange={(e) => setName(e.target.value)} />
                </div>
                <div className='field disabled'>
                    <label htmlFor='profile-email'>Email</label>
                    <input id='profile-email' type='email' value={currentUser.email ?? ''} disabled readOnly />
                </div>
                <ProfileIcon name={name} icon={icon} photoURL={currentUser.photoURL}
                    onChange={setIcon} onError={setAlertMessage} />
                <div className='buttons'>
                    {editing
                        ? <button className='g-button' type='button' onClick={() => navigate('/home')}>Cancel</button>
                        : <button className='g-button' type='button' onClick={() => navigate('/logout')}>Sign out</button>
                    }
                    {loading
                        ? <button className='g-button save-profile-button disabled' type='button'><ThreeDotsLoader /></button>
                        : <button className='g-button save-profile-button primary' type='submit'>Save profile</button>
                    }
                </div>
            </form>
            <ModalAlert show={alertMessage} cancel={() => setAlertMessage(null)} message={alertMessage} />
        </div>
    )
}

export default ProfilePage
